/* Copyright (c) 2020-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import { Else, If, Then } from "react-if";
import { FlexContainer, SVGIcon, Text, theme } from "@nordcloud/gnui";
import { getDroppableCss } from "~/components/Styled";
import { Items, reorder } from "~/hooks/useComponentsSelector";
import { isNonEmpty } from "~/tools";
import { Item } from "~/types";
import { ComponentDraggableList } from "../../DraggableList/DraggableList";

type Props = {
  items: Items;
  visibleCount?: number;
  handleDragEnd: (result: DropResult) => void;
  handleRemoveItem: (item: Item) => void;
  handleReorder: (ids: string[]) => void;
};

const DROPPABLE_ITEM_HEIGHT = 4.625; // in rem

export function ResourceDragDrop({
  items,
  visibleCount,
  handleDragEnd,
  handleRemoveItem,
  handleReorder,
}: Props) {
  const handleDrag = (result: DropResult) => {
    handleDragEnd(result);

    const reorderedResources = reorder<string>(
      items["SELECTED_ITEMS"].map((item) => item.id) ?? [],
      result.source.index,
      result.destination?.index ?? -1
    );

    handleReorder(reorderedResources);
  };

  return (
    <>
      <If condition={isNonEmpty(items.SELECTED_ITEMS)}>
        <Then>
          <div
            css={{
              minHeight: `calc(${visibleCount} * ${DROPPABLE_ITEM_HEIGHT}rem)`, // Prevent collapsing when moving item
            }}
          >
            <DragDropContext onDragEnd={handleDrag}>
              <Droppable droppableId="SELECTED_ITEMS">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    css={getDroppableCss(true, snapshot.isDraggingOver)}
                  >
                    <ComponentDraggableList
                      items={
                        visibleCount
                          ? items["SELECTED_ITEMS"].slice(0, visibleCount)
                          : items["SELECTED_ITEMS"]
                      }
                      removeItem={handleRemoveItem}
                    />
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </Then>
        <Else>
          <FlexContainer
            alignContent="center"
            justifyContent="center"
            css={{ minHeight: "22rem" }}
          >
            <FlexContainer direction="column" css={{ textAlign: "center" }}>
              <SVGIcon size="xxl" name="bulb" />
              <Text mt={theme.spacing.spacing04}>
                There are no resources at the moment. <br /> Please add
                resources.
              </Text>
            </FlexContainer>
          </FlexContainer>
        </Else>
      </If>
    </>
  );
}
